import React, { useEffect, useState } from 'react';

import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import AccountBlockedModal from '@components/common/Modals/AccountBlockedModal/AccountBlockedModal';
import Spinner from '@components/common/Spinner/Spinner';
import Text from '@components/common/Text/Text';
import authService from '@services/domain/Lpc/AuthService';

import css from './ResendBloc.module.scss';

export type ResendBlocProps = {
    onSetSmsTimer?: (number) => void;
    isConnected?: boolean;
    email?: string;
};

const ResendBloc = ({ onSetSmsTimer, isConnected, email }: ResendBlocProps) => {
    const { t } = useTranslateLpc('lpc-double-authentication');
    const { addToast } = useToasts();
    const [isAccountBlockedModalVisible, setIsAccountBlockedModalVisible] = useState<boolean>(false);
    const [smsTimerSeconds, setSmsTimerSeconds] = useState<number>(0);
    const [smsTimerMinutes, setSmsTimerMinutes] = useState<number>(2);
    const [isSendingSms, setIsSendingSms] = useState<boolean>(false);

    useEffect(() => {
        countdown();
    }, []);

    const countdown = () => {
        let secondsLeft = 120;
        const timer = setInterval(() => {
            secondsLeft--;
            setSmsTimerMinutes(Math.trunc(secondsLeft / 60));
            setSmsTimerSeconds(secondsLeft % 60);
            onSetSmsTimer && onSetSmsTimer(secondsLeft);
            if (secondsLeft < 1) {
                clearInterval(timer);
            }
        }, 1000);
    };

    const resendSms = () => {
        setIsSendingSms(true);
        authService
            .resendSms(isConnected, email)
            .then(() => {
                addToast(t(`success.SMS_SENT`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            })
            .catch((err) => {
                err.code === 'SMS_LIMIT_EXCEEDED'
                    ? setIsAccountBlockedModalVisible(true)
                    : addToast(t(`errors.${err.code}`), {
                          appearance: 'error',
                          autoDismiss: true,
                      });
            })
            .finally(() => {
                setIsSendingSms(false);
                countdown();
            });
    };
    return (
        <div className={css.resend_bloc}>
            {smsTimerSeconds === 0 && smsTimerMinutes === 0 ? (
                <Text variant={'caption_02'} color={'txt-primary'}>
                    {t('connection.sms.resend.desc')}
                </Text>
            ) : smsTimerSeconds === 1 ? (
                <Text variant={'caption_02'} color={'txt-primary'}>
                    {t('connection.sms.resend.timer', { minutes: smsTimerMinutes, seconds: smsTimerSeconds })}
                </Text>
            ) : (
                <Text variant={'caption_02'} color={'txt-primary'}>
                    {t('connection.sms.resend.timer_plural', { minutes: smsTimerMinutes, seconds: smsTimerSeconds })}
                </Text>
            )}

            {smsTimerSeconds === 0 && smsTimerMinutes === 0 && (
                <Text
                    variant={'caption_02'}
                    color={smsTimerSeconds === 0 && smsTimerMinutes === 0 ? 'brand-primary' : 'ui-secondary'}
                    customClass={css.resend}
                >
                    <span onClick={() => smsTimerSeconds === 0 && smsTimerMinutes === 0 && resendSms()}>
                        {t('connection.sms.resend.cta')}
                    </span>
                </Text>
            )}
            {isSendingSms && <Spinner customClass={css.spinner} />}
            {isAccountBlockedModalVisible && (
                <AccountBlockedModal
                    isAccountBlockedModalVisible={isAccountBlockedModalVisible}
                    onHide={() => setIsAccountBlockedModalVisible(false)}
                />
            )}
        </div>
    );
};

export default ResendBloc;
